import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiArrowRight } = FiIcons;

const Card = ({
  title,
  description,
  image,
  badge,
  tags = [],
  link,
  icon,
  result,
  stats,
  className = "",
  variant = "default",
  onClick,
  children,
  ...motionProps
}) => {
  const cardClasses = {
    default: "bg-white rounded-2xl shadow-lg hover:shadow-xl border border-slate-200",
    highlight: "bg-gradient-to-br from-orange-50 to-orange-100 rounded-2xl shadow-lg hover:shadow-xl border border-orange-200",
    dark: "bg-slate-800 rounded-2xl shadow-lg hover:shadow-xl border border-slate-700",
    minimal: "bg-white rounded-lg shadow-md hover:shadow-lg border border-slate-100"
  };

  const CardContent = () => (
    <motion.div
      {...motionProps}
      className={`group transition-all duration-300 overflow-hidden ${cardClasses[variant]} ${className}`}
    >
      {image && (
        <div className="relative overflow-hidden">
          <img
            src={image}
            alt={title}
            className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
          />
          {badge && (
            <div className="absolute top-4 left-4">
              <span className="bg-orange-500 text-white px-3 py-1 rounded-full text-xs font-semibold">
                {badge}
              </span>
            </div>
          )}
        </div>
      )}
      
      <div className="p-6">
        {icon && (
          <div className="w-16 h-16 bg-orange-100 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-orange-500 transition-all duration-300">
            <SafeIcon icon={icon} className="text-orange-600 group-hover:text-white text-2xl transition-colors duration-300" />
          </div>
        )}
        
        <h3 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-orange-600 transition-colors duration-300">
          {title}
        </h3>
        
        {description && (
          <p className="text-slate-600 mb-4 leading-relaxed">
            {description}
          </p>
        )}
        
        {result && (
          <div className="mb-4">
            <div className="text-sm font-semibold text-slate-900 mb-1">Result:</div>
            <div className="text-orange-600 font-medium">{result}</div>
          </div>
        )}
        
        {tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {tags.map((tag, index) => (
              <span
                key={index}
                className="px-3 py-1 bg-slate-100 text-slate-600 text-xs rounded-full"
              >
                {tag}
              </span>
            ))}
          </div>
        )}
        
        {stats && (
          <div className="grid grid-cols-2 gap-4 mb-4">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-lg font-bold text-slate-900">{stat.value}</div>
                <div className="text-slate-600 text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        )}
        
        {children}
        
        {link && (
          <div className="flex items-center space-x-2 text-orange-600 group-hover:text-orange-700 font-medium transition-colors duration-300 mt-auto">
            <span>{link.text || "Learn More"}</span>
            <SafeIcon icon={FiArrowRight} className="group-hover:translate-x-1 transition-transform duration-300" />
          </div>
        )}
      </div>
    </motion.div>
  );

  if (link?.to) {
    return (
      <Link to={link.to} onClick={onClick}>
        <CardContent />
      </Link>
    );
  }

  if (onClick) {
    return (
      <button onClick={onClick} className="w-full text-left">
        <CardContent />
      </button>
    );
  }

  return <CardContent />;
};

export default Card;